import { useCallback, useEffect, useMemo, useState } from "react";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import substitutePhoto from "../../../../images/profile_substitute.svg";
import { deleteRecording as deleteSessionRecording } from "./proctoringResultViewHelper";

type UseResultComponentsProps = Pick<
	ProctoringResultViewProps,
	"setShowStudentResults" | "setSelectedStudent" | "studentList" | "selectedStudent" | "setProctoringSessions"
>;

export default function useResultComponents({
	setShowStudentResults,
	setSelectedStudent,
	studentList,
	setProctoringSessions,
	selectedStudent,
}: UseResultComponentsProps) {
	const [emailModalShowing, setEmailModalShowing] = useState<boolean>(false);
	const [isDeleteConfirmationModalShown, setIsDeleteConfirmationModalShown] = useState<
		"recording" | "roomscan" | undefined
	>();

	const [mediaGridHeight, setMediaGridHeight] = useState(0);

	const showBackButton = useMemo(() => Boolean(setShowStudentResults), [setShowStudentResults]);

	const handleBackButton = useCallback(() => {
		setShowStudentResults && setShowStudentResults(false);
		setSelectedStudent && setSelectedStudent(undefined);
	}, [setShowStudentResults, setSelectedStudent]);

	const toggleEmailModal = useCallback((showing: boolean) => {
		setEmailModalShowing(showing);
	}, []);

	const toggleDeleteConfirmationModal = useCallback((videoType: "recording" | "roomscan" | undefined) => {
		setIsDeleteConfirmationModalShown(videoType);
	}, []);

	const handleImageError = useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (event.target instanceof HTMLImageElement && event.target.src !== substitutePhoto) {
			event.target.src = substitutePhoto;
		}
	}, []);

	const handleStudentChange = useCallback(
		(studentId?: number, dir?: "next" | "prev") => {
			if (dir === "next" || dir === "prev") {
				const currentIndex = studentList.findIndex((student) => student.id === selectedStudent);
				const numStudents = studentList.length;
				const nextIndex =
					dir === "next" ? (currentIndex + 1) % numStudents : (currentIndex - 1 + numStudents) % numStudents;
				const nextStudentId = studentList[nextIndex].id;
				setSelectedStudent(nextStudentId);
				return;
			}

			setSelectedStudent(studentId);
		},
		[selectedStudent, setSelectedStudent, studentList],
	);

	const handleSelectPreviousStudent = useCallback(() => {
		handleStudentChange(undefined, "prev");
	}, [handleStudentChange]);

	const handleSelectNextStudent = useCallback(() => {
		handleStudentChange(undefined, "next");
	}, [handleStudentChange]);

	const handleSelectStudent = useCallback(
		(studentId?: number) => () => {
			handleStudentChange(studentId);
		},
		[handleStudentChange],
	);

	const deleteRecording = useCallback(
		async (sessionUuid: string) => {
			const isRecording = isDeleteConfirmationModalShown === "recording";
			await deleteSessionRecording(sessionUuid, isRecording ? "recording" : "roomScan");

			// update the appropriate deleted flag in props.proctoringSessions without rerendering the parent component.
			setProctoringSessions?.((prevSessions) => {
				if (prevSessions) {
					const sessionIndex = prevSessions.findIndex((session) => session.uuid === sessionUuid);
					if (sessionIndex !== -1) {
						const session = prevSessions[sessionIndex];
						if (isRecording) {
							session.isWebcamAndScreenCaptureDeleted = true;
						} else {
							session.isRoomScanDeleted = true;
						}
					}
				}
				return prevSessions;
			});
			return isDeleteConfirmationModalShown;
		},
		[isDeleteConfirmationModalShown, setProctoringSessions],
	);

	useEffect(() => {
		const mediaGrid = document.getElementById("media_grid");

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.target === mediaGrid) {
					// + 70 because the timestampLinks section is positioned higher than the mediaGrid
					setMediaGridHeight(entry.contentRect.height + 70);
				}
			}
		});

		if (mediaGrid) {
			observer.observe(mediaGrid);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	return {
		back: {
			showBackButton,
			handleBackButton,
		},
		modal: {
			emailModalShowing,
			toggleEmailModal,
			isDeleteConfirmationModalShown,
			toggleDeleteConfirmationModal,
			deleteRecording,
		},
		image: {
			handleImageError,
		},
		selectStudent: {
			handleSelectPreviousStudent,
			handleSelectNextStudent,
			handleSelectStudent,
		},
		mediaGrid: {
			mediaGridHeight,
		},
	};
}
